/**
 * Crunch Design System — Notification
 *
 * Figma component set (node 1268:233)
 *
 * Usage:
 *   <Notification type="success" heading="Saved" description="Your changes have been saved." />
 *   <Notification type="danger" layout="banner" heading="Error" description="Something went wrong." onDismiss={() => {}} />
 */
import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faAsterisk,
    faCircleExclamation,
    faCircleCheck,
    faCircleInfo,
    faXmark,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

const ICON_MAP: Record<string, IconDefinition> = {
    primary: faAsterisk,
    danger: faCircleExclamation,
    warning: faCircleExclamation,
    success: faCircleCheck,
    info: faCircleInfo,
};

export function Notification({
    type = "primary",
    layout = "toast",
    heading = "Heading",
    description = "Description",
    showButton = false,
    buttonLabel = "Okay",
    onButtonClick,
    dismissable = true,
    onDismiss,
    className,
    ref,
    ...rest
}: {
    type?: "primary" | "danger" | "warning" | "success" | "info";
    layout?: "toast" | "banner";
    heading?: string;
    description?: string;
    showButton?: boolean;
    buttonLabel?: string;
    onButtonClick?: () => void;
    dismissable?: boolean;
    onDismiss?: () => void;
    className?: string;
    ref?: React.Ref<HTMLDivElement>;
    [key: string]: any;
}) {
    const cls = [
        "cr-notification",
        `cr-notification--${type}`,
        `cr-notification--${layout}`,
        className,
    ]
        .filter(Boolean)
        .join(" ");

    const icon = ICON_MAP[type] || ICON_MAP.primary;

    const actionButton = showButton && (
        <button
            className="cr-btn cr-btn--important"
            type="button"
            onClick={onButtonClick}
        >
            <span className="cr-btn__label">{buttonLabel}</span>
        </button>
    );

    const dismissButton = dismissable && (
        <button
            className="cr-btn-icon cr-btn-icon--floating cr-notification__dismiss"
            type="button"
            aria-label="Dismiss"
            onClick={onDismiss}
        >
            <FontAwesomeIcon icon={faXmark} />
        </button>
    );

    return (
        <div className={cls} role="alert" ref={ref} {...rest}>
            <span className="cr-notification__icon">
                <FontAwesomeIcon icon={icon} />
            </span>

            <div className="cr-notification__text">
                {heading && <p className="cr-notification__heading">{heading}</p>}
                {description && (
                    <p className="cr-notification__description">{description}</p>
                )}
                {layout === "toast" && actionButton}
            </div>

            {layout === "toast" && dismissButton}

            {layout === "banner" && (
                <div className="cr-notification__actions">
                    {actionButton}
                    {dismissButton}
                </div>
            )}
        </div>
    );
}
